import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTabLabel,
  SidebarTab,
  SidebarTabs,
  SidebarFooter,
} from '@trueprodigy/components';

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withStateHandlers } from 'recompose';
import styled from 'styled-components';

import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import HeaderWrapper from '@components/HeaderWrapper';
import * as globalActions from '@actions/global';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';
import { resetActiveRecords } from '../actions/global';
import {
  fetchAppraisalYears,
  fetchCurrentAppraisalYear,
} from '@modules/appraisal/actions/global';
import { selectedMarketAreaSelector } from '../selectors/global';

import { AnalysisRoutes } from '../constants';
import ability, { defineRules } from '@config/permissions/ability';

import versionIcon from '../../../assets/images/PA_Version.png';
import screenIcon from '../../../assets/images/PA_Print-Screen.png';
import analysisLogo from '@assets/images/true-prodigy_analysis.png';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';

const parseHeaderTitle = ({ pathname }, activeMarketArea) => {
  if (pathname === AnalysisRoutes.AnalysisMassUpdate) return 'Mass Update Log';
  if (pathname === AnalysisRoutes.AnalysisMarketAreaSearch)
    return 'Market Area Search';
  if (pathname === AnalysisRoutes.AnalysisDefaults)
    return 'Analysis Default Settings';
  if (pathname === AnalysisRoutes.AnalysisEquityComparables)
    return 'Equity Comparables';
  if (pathname === AnalysisRoutes.AnalysisSalesComparables)
    return 'Sales Comparables';
  if (activeMarketArea) return 'Market Area';
  if (pathname === AnalysisRoutes.AnalysisStratifications)
    return 'Stratifications';
  return '';
};

const PageWrapper = ({
  activeMarketArea,
  activeTab,
  children,
  closeTab,
  history,
  isSidebarVisible,
  location,
  setActiveTab,
  toggleSidebar,
  userRights,
  userStatus,
}) => {
  const isReadOnly = userStatus.get('readOnly');

  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  return (
    <Container>
      <Helmet>
        <title>Analysis</title>
      </Helmet>
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={analysisLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="Analysis" />
            }
          />
          {activeMarketArea && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Market Area"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AnalysisRoutes.AnalysisMarketAreaSearch);
                  }}
                />
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 && (
          <>
            <SidebarItem
              link={AnalysisRoutes.AnalysisMarketAreaSearch}
              title="Market Area Search"
            />
            <SidebarItem
              link={AnalysisRoutes.AnalysisMassUpdate}
              title="Mass Update Log"
            />
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem defaultOpen={false} nested title="Settings">
                <SidebarItem
                  link={AnalysisRoutes.AnalysisDefaults}
                  title="Analysis Defaults"
                />
                <SidebarItem
                  defaultOpen={false}
                  nested
                  title="Residential Settings"
                >
                  <SidebarItem
                    link={AnalysisRoutes.AnalysisEquityComparables.replace(
                      ':type',
                      'residential',
                    )}
                    title="Equity Comparables"
                    wrapperClassName="deepNested"
                  />
                  <SidebarItem
                    link={AnalysisRoutes.AnalysisSalesComparables.replace(
                      ':type',
                      'residential',
                    )}
                    title="Sales Comparables"
                    wrapperClassName="deepNested"
                  />
                </SidebarItem>
                <SidebarItem
                  defaultOpen={false}
                  nested
                  title="Commercial Settings"
                >
                  <SidebarItem
                    link={AnalysisRoutes.AnalysisEquityComparables.replace(
                      ':type',
                      'commercial',
                    )}
                    title="Equity Comparables"
                    wrapperClassName="deepNested"
                  />
                  <SidebarItem
                    link={AnalysisRoutes.AnalysisSalesComparables.replace(
                      ':type',
                      'commercial',
                    )}
                    title="Sales Comparables"
                    wrapperClassName="deepNested"
                  />
                </SidebarItem>
                <SidebarItem
                  link={AnalysisRoutes.SalesAnalysisTransitions}
                  title="Sales Analysis Translations"
                />
              </SidebarItem>
            )}
            <SidebarItem
              defaultOpen={false}
              nested
              title="Sales Stratification"
            >
              <SidebarItem
                link={AnalysisRoutes.AnalysisStratifications}
                title="Stratifications"
              />
              <SidebarItem
                link={AnalysisRoutes.AnalysisStrata}
                title="Sales Strata"
              />
              <SidebarItem
                link={AnalysisRoutes.AnalysisStrataRuns}
                title="Sales Stratification Report"
              />
            </SidebarItem>
          </>
        )}
        {activeTab === 1 && activeMarketArea && (
          <SidebarItem
            link={`/analysis/market-area/${encodeURIComponent(
              activeMarketArea.marketArea,
            )}/${activeMarketArea.marketAreaYear}/summary`}
            title="Summary"
          />
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location, activeMarketArea)}
              toggleSidebar={toggleSidebar}
            >
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem
                icon={versionIcon}
                title="Smart List"
                onClick={() =>
                  history.push(
                    '/analysis/smart-lists?module=Prodigy%20Analysis',
                  )
                }
              />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
    </Container>
  );
};

PageWrapper.propTypes = {
  activeMarketArea: PropTypes.object,
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  fetchAppraisalYears: PropTypes.func,
  fetchCurrentAppraisalYear: PropTypes.func,
  history: PropTypes.object.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  resetActiveRecords: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  userRights: ImmutablePropTypes.list,
  userStatus: PropTypes.any,
};

const Container = styled.div`
  .deepNested {
    a {
      margin-left: 20px;
    }
  }
`;

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = (state) => ({
  activeMarketArea: selectedMarketAreaSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userRights: userRightsSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
  fetchAppraisalYears: fetchAppraisalYears,
  fetchCurrentAppraisalYear: fetchCurrentAppraisalYear,
  resetActiveRecords,
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab:
      (_, { resetActiveRecords }) =>
      () => {
        resetActiveRecords();
        return { activeTab: 0 };
      },
  }),
  lifecycle({
    componentDidMount() {
      this.props.setActiveTab(null, 0);
      this.props.fetchAppraisalYears();
      this.props.fetchCurrentAppraisalYear();
    },
    componentDidUpdate(prevProps) {
      if (
        this.props.activeMarketArea !== prevProps.activeMarketArea &&
        this.props.activeMarketArea
      ) {
        this.props.setActiveTab(null, 1);
      }
    },
  }),
);

export default enhance(PageWrapper);
